import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";
import { Button, StatusPill, Select } from "@/components/kit";

const LABELS = { meeting: "General meeting attendance", event: "Event attendance", payment: "Membership payment", donation: "Donation (Phase 12)" };
const inputCls = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]";

function RewardForm({ inventory, initial, onClose, onDone }) {
  const [f, setF] = useState(initial || { name: "", description: "", point_cost: 50, inventory_id: "", active: true });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required.");
    const body = { ...f, point_cost: parseInt(f.point_cost) || 0, inventory_id: f.inventory_id || null };
    try {
      if (initial?.reward_id) await api.put(`/rewards/${initial.reward_id}`, body);
      else await api.post("/rewards", body);
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-4 max-w-xl space-y-3" data-testid="reward-form">
      <div><label className="text-[13px] font-semibold block mb-1">Name</label><input data-testid="reward-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></div>
      <div><label className="text-[13px] font-semibold block mb-1">Description</label><input data-testid="reward-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-[13px] font-semibold block mb-1">Point cost</label><input data-testid="reward-cost" type="number" min="0" value={f.point_cost} onChange={(e) => setF({ ...f, point_cost: e.target.value })} className={inputCls} /></div>
        <div><label className="text-[13px] font-semibold block mb-1">Linked inventory (optional)</label>
          <Select data-testid="reward-inventory" value={f.inventory_id || ""} onChange={(e) => setF({ ...f, inventory_id: e.target.value })} className={inputCls}>
            <option value="">None (no stock tracking)</option>
            {inventory.map((i) => <option key={i.inventory_id} value={i.inventory_id}>{i.name}{i.variant ? ` · ${i.variant}` : ""} ({i.quantity})</option>)}
          </Select></div>
      </div>
      <label className="flex items-center gap-2 text-[14px]"><input type="checkbox" data-testid="reward-active" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active (visible to members)</label>
      <div className="flex gap-2"><Button data-testid="reward-save" onClick={save}>Save reward</Button><Button variant="secondary" onClick={onClose}>Cancel</Button></div>
    </div>
  );
}

function BackfillTool() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);

  const runPreview = async () => {
    setLoading(true); setDone(null);
    try { const r = await api.get("/points/backfill/preview"); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not run dry run"); }
    finally { setLoading(false); }
  };
  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post("/points/backfill/apply", { confirm: true });
      setDone(r.data); toast.success(`Awarded ${r.data.awarded} check-in(s), ${r.data.total_points} points`);
      runPreview();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
    finally { setApplying(false); }
  };

  const Section = ({ title, rows, tone }) => (
    <div className="mt-3">
      <div className={`text-[13px] font-bold ${tone}`}>{title} ({rows.length})</div>
      {rows.length > 0 && (
        <div className="mt-1 bg-warm-cream border border-ink/10 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
          {rows.map((r) => (
            <div key={r.checkin_id} className="flex items-center justify-between gap-3 px-3 py-2 border-b border-ink/8 last:border-0 text-[13px]">
              <div className="min-w-0">
                <div className="font-semibold truncate">{r.member || r.name}</div>
                <div className="text-ink/50 text-[12px]">{r.event_title || "General meeting"} · {r.checkin_kind}{r.reason ? ` · ${r.reason}` : ""}</div>
              </div>
              <span className="shrink-0 font-semibold text-dark-olive">{r.points ? `+${r.points}` : "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5 max-w-2xl mb-6" data-testid="points-backfill">
      <h3 className="font-zilla text-xl font-semibold mb-1">Backfill attendance points</h3>
      <p className="text-[14px] text-ink/65 mb-3">Awards the configured points for existing check-ins that never earned them. Step 1 is a dry run — nothing is awarded until you confirm. Safe to run more than once (no double awards).</p>
      <Button data-testid="backfill-preview" variant="secondary" onClick={runPreview} disabled={loading}>{loading ? "Scanning…" : "Run dry run"}</Button>

      {preview && (
        <div className="mt-4" data-testid="backfill-results">
          <div className="text-[14px]">Scanned <b>{preview.total}</b> check-ins · <b>{preview.would_award.length}</b> to award (<b>{preview.total_points_to_award}</b> pts) · <b>{preview.already_awarded.length}</b> already done · <b>{(preview.duplicate || []).length}</b> duplicate · <b>{preview.unmatched.length}</b> unmatched</div>
          <Section title="Will award" rows={preview.would_award} tone="text-dark-olive" />
          <Section title="Already awarded (skipped)" rows={preview.already_awarded} tone="text-ink/50" />
          <Section title="Duplicate — repeat check-in, skipped" rows={preview.duplicate || []} tone="text-gold" />
          {preview.no_points_set?.length > 0 && <Section title="Matched but point value is 0" rows={preview.no_points_set} tone="text-gold" />}
          <Section title="Could not match a member" rows={preview.unmatched} tone="text-maroon" />

          {preview.would_award.length > 0 && (
            <Button data-testid="backfill-apply" className="mt-4" onClick={apply} disabled={applying}>
              {applying ? "Awarding…" : `Confirm & award ${preview.would_award.length} check-in(s)`}
            </Button>
          )}
          {done && <div className="mt-3 text-[14px] text-dark-olive font-semibold" data-testid="backfill-done">Done — awarded {done.awarded} check-in(s), {done.total_points} points.</div>}
        </div>
      )}
    </div>
  );
}

function RecountVisitsTool() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);
  const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

  const runPreview = async () => {
    setLoading(true); setDone(null);
    try { const r = await api.get("/visits/recount/preview"); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not run dry run"); }
    finally { setLoading(false); }
  };
  const apply = async () => {
    setApplying(true);
    try { const r = await api.post("/visits/recount/apply", { confirm: true }); setDone(r.data); toast.success(`Corrected ${r.data.updated} visitor(s)`); runPreview(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
    finally { setApplying(false); }
  };

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5 max-w-2xl mb-6" data-testid="visits-recount">
      <h3 className="font-zilla text-xl font-semibold mb-1">Recount visits</h3>
      <p className="text-[14px] text-ink/65 mb-3">Recounts each visitor's visits as distinct check-in events — one per person per event or per day, ignoring repeat scans in the same event. Step 1 is a dry run; nothing changes until you confirm. Never deletes a check-in.</p>
      <Button data-testid="recount-preview" variant="secondary" onClick={runPreview} disabled={loading}>{loading ? "Scanning…" : "Run dry run"}</Button>

      {preview && (
        <div className="mt-4" data-testid="recount-results">
          <div className="text-[14px] mb-2">Scanned <b>{preview.total}</b> visitor(s) · <b>{preview.changed}</b> will change</div>
          <div className="bg-warm-cream border border-ink/10 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-2 text-[12px] font-bold text-ink/55 border-b border-ink/10 sticky top-0 bg-warm-cream">
              <span>Visitor</span><span className="text-right">Current</span><span className="text-right">Corrected</span><span className="text-right">Last visit</span>
            </div>
            {preview.rows.map((r) => (
              <div key={r.visitor_id} data-testid={`recount-row-${r.visitor_id}`} className={`grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-2 border-b border-ink/8 last:border-0 text-[13px] ${r.current !== r.corrected ? "bg-gold/10" : ""}`}>
                <span className="font-semibold truncate">{r.name}</span>
                <span className="text-right">{r.current}</span>
                <span className={`text-right font-semibold ${r.current !== r.corrected ? "text-maroon" : "text-ink/50"}`}>{r.corrected}</span>
                <span className="text-right text-ink/55">{fmt(r.last_visit)}</span>
              </div>
            ))}
            {preview.rows.length === 0 && <div className="px-3 py-4 text-center text-ink/50 text-[13px]">No visitor check-ins found.</div>}
          </div>
          {preview.changed > 0 && <Button data-testid="recount-apply" className="mt-4" onClick={apply} disabled={applying}>{applying ? "Applying…" : `Confirm & correct ${preview.changed} visitor(s)`}</Button>}
          {done && <div className="mt-3 text-[14px] text-dark-olive font-semibold" data-testid="recount-done">Done — corrected {done.updated} visitor(s).</div>}
        </div>
      )}
    </div>
  );
}

export default function PointsRewards({ me }) {
  const canEdit = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.points_rewards) || ["edit", "full"].includes(me?.permissions?.modules);
  const [cfg, setCfg] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | reward

  const load = useCallback(async () => {
    const [c, r] = await Promise.all([api.get("/points/config"), api.get("/rewards", { params: { all: 1 } })]);
    setCfg(c.data); setRewards(r.data.items || []);
    try { const inv = await api.get("/inventory"); setInventory(inv.data.items || []); } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => { try { await api.put("/points/config", cfg); toast.success("Point values saved"); } catch (e) { toast.error("Could not save"); } };
  const deactivate = async (rid) => { if (!window.confirm("Deactivate this reward?")) return; try { await api.delete(`/rewards/${rid}`); toast.success("Deactivated"); load(); } catch (e) { toast.error("Could not deactivate"); } };

  if (!cfg) return <div className="text-ink/60 py-8">Loading…</div>;
  return (
    <div data-testid="points-rewards-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="points.title" screen="Points">Points &amp; Rewards</Txt></h1>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="points.subtitle" screen="Points">Points post automatically when members attend, pay, or donate. Set the values and manage the reward catalog here.</Txt></p>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 max-w-md mb-6">
        <h3 className="font-zilla text-xl font-semibold mb-3">Point values</h3>
        {["meeting", "event", "payment", "donation"].map((k) => (
          <div key={k} className="flex items-center justify-between gap-3 py-2 border-b border-ink/8 last:border-0">
            <span className="text-[15px]">{LABELS[k]}</span>
            <input type="number" min="0" data-testid={`points-${k}`} disabled={!canEdit} value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: parseInt(e.target.value) || 0 })} className="w-24 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[15px] text-right disabled:opacity-60" />
          </div>
        ))}
        {canEdit && <Button data-testid="points-save" className="mt-4" onClick={save}>Save point values</Button>}
        <p className="text-[13px] text-ink/55 mt-3 pt-3 border-t border-ink/8" data-testid="volunteer-points-note">Volunteer points are set per volunteer role when an event is created.</p>
      </div>

      {me?.user?.is_super_admin && <BackfillTool />}
      {me?.user?.is_super_admin && <RecountVisitsTool />}

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-zilla text-xl font-semibold">Reward catalog</h3>
        {canEdit && !editing && <Button variant="olive" data-testid="reward-add" onClick={() => setEditing({})}>Add reward</Button>}
      </div>
      {editing && <RewardForm inventory={inventory} initial={editing.reward_id ? editing : null} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="reward-catalog-admin">
        {rewards.length === 0 && <div className="bg-cream border border-ink/15 rounded-xl p-8 text-center text-ink/55 sm:col-span-2">No rewards yet.</div>}
        {rewards.map((rw) => (
          <div key={rw.reward_id} data-testid={`reward-admin-${rw.reward_id}`} className={`bg-cream border border-ink/15 rounded-xl p-4 ${!rw.active ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[16px] font-semibold flex items-center gap-2"><Icon name="Gift" className="w-4 h-4 text-gold" />{rw.name}{!rw.active && <StatusPill tone="gray" className="text-[11px]">inactive</StatusPill>}</div>
                {rw.description && <div className="text-[13px] text-ink/60">{rw.description}</div>}
                <div className="text-[13px] text-ink/55 mt-1">{rw.point_cost} pts{rw.inventory ? ` · linked: ${rw.inventory.name} (${rw.inventory.quantity} in stock)` : ""}</div>
              </div>
            </div>
            {canEdit && (
              <div className="flex gap-2 mt-3 pt-3 border-t border-ink/8">
                <Button variant="secondary" className="min-h-[38px] py-1.5 text-[13px]" data-testid={`reward-edit-${rw.reward_id}`} onClick={() => setEditing(rw)}>Edit</Button>
                {rw.active && <Button variant="danger" className="min-h-[38px] py-1.5 text-[13px]" data-testid={`reward-deactivate-${rw.reward_id}`} onClick={() => deactivate(rw.reward_id)}>Deactivate</Button>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
